import React, {useEffect, useState} from 'react';
import styled, {css} from 'styled-components';
import {Spinner} from './Spinner';

const SpinnerLoaderContainer = styled.div`
    position: relative;
    width: 100%;
    padding: var(--space-sm) 0;

    ${({fixed}) => {
        if (fixed)
            return `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        `;
    }}

    ${({center}) => {
        if (center === 'container')
            return css`
                position: unset;
                height: unset;
                width: unset;
                > div {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }
            `;

        if (center === 'page')
            return css`
                background: var(--container-background-color-backdrop);
                position: fixed;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                z-index: 4000;
                > div {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: var(--white-00);
                    ${Spinner} {
                        color: var(--white-00);
                    }
                }
            `;
    }}
`;

/**
 * This component will display a loading animation.
 *
 * @param {object} param0
 *
 * @param {React.ReactNode} [param0.children]
 *
 * @param {boolean} [param0.isLoading]
 * Controls whether the loading animation should be displayed.
 *
 * @param {boolean} [param0.fixed]
 *
 *
 * @param {'page' | 'container'} [param0.center]
 * The `center` props will center the loader animation. There are two versions:
 *
 * ### Page:
 * The loading animation will be centered in the middle of the entire page and will also display a backdrop.
 *
 *  @example
 * ```jsx
 *   <Page>
 *      <SpinnerLoader center="container">Loading</SpinnerLoader>
 *      <Main>Main content</Main>
 *   </Page>
 * `;
 * ```
 *
 * ### Container: The loading animation component will be centered within the content or container it is in.
 *
 *  @example
 * ```jsx
 *   <Card>
 *      <SpinnerLoader center="container">Loading</SpinnerLoader>
 *      // (Card component)
 *   </Card>
 * `;
 * ```
 */
const SpinnerLoader = ({children, center, fixed, isLoading}) => {
    const [showLoading, setShowLoading] = useState(false);

    useEffect(() => {
        // Only set the loading state if loading is true
        if (isLoading) {
            const timer = setTimeout(() => {
                setShowLoading(true);
            }, 500);

            // Cleanup function to reset state and clear timeout when not loading
            return () => {
                clearTimeout(timer);
                setShowLoading(false);
            };
        } else {
            setShowLoading(false);
        }
    }, [isLoading]);

    return (
        <>
            {showLoading && (
                <>
                    {center !== 'container' && (
                        <SpinnerLoaderBlock center={center} fixed={fixed}>
                            {children}
                        </SpinnerLoaderBlock>
                    )}
                    {center === 'container' && (
                        <SpinnerBlock>
                            <SpinnerLoaderBlock center={center} fixed={fixed}>
                                {children}
                            </SpinnerLoaderBlock>
                        </SpinnerBlock>
                    )}
                </>
            )}
        </>
    );
};

const SpinnerLoaderBlock = ({center, fixed, children}) => (
    <SpinnerLoaderContainer center={center} fixed={fixed}>
        <div>
            <Spinner />
            <LoadingTxt>{children}</LoadingTxt>
        </div>
    </SpinnerLoaderContainer>
);

const LoadingTxt = styled.div`
    margin-top: var(--space-xxl);
    text-align: center;
`;

const SpinnerBlock = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;
export {SpinnerLoader, SpinnerLoaderContainer};
